import React, { useState, useEffect } from "react";
import { useTranslation } from "../../i18n/TranslationProvider";

// Hooks
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import useListSelector from "../../lib/useListSelector";
import StrokeListSelecter from "../UIComponents/StrokeListSelecter";
import { yupResolver } from "@hookform/resolvers";

// Bootstrap
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";

// Validation Rules
import PatientSchema from "./validations/registerPatientValidation2";

import { getPatientList, registerPatient2 } from "../../api/auth";
import { createGoals, getPatientTypes } from "../../api/TBIRequests";

// Components
import Sallie from "../UIComponents/Sallie";
import LabelledInput from "../UIComponents/LabelledInput";
import SubmitButton from "../UIComponents/SubmitButton";
import OnboardingStepHeader from "./OnboardingStepHeader";

const PatientInfo = ({
  setOnboardingPercent,
  setPatientList,
  user,
  setPatient,
}) => {
  const { t } = useTranslation();

  const types = [
    {
      category: "TBI/Concussion",
      items: [
        t("basicinfo.primary_diagnosis.tbi.acute"),
        t("basicinfo.primary_diagnosis.tbi.chronic"),
        t("basicinfo.primary_diagnosis.tbi.pcs"),
      ],
    },
    {
      category: "Dementia",
      items: [
        t("basicinfo.primary_diagnosis.dementia.alzheimers"),
        t("basicinfo.primary_diagnosis.dementia.lewy"),
        t("basicinfo.primary_diagnosis.dementia.parkinsons"),
      ],
    },
    {
      category: "Stroke",
      items: [
        t("basicinfo.primary_diagnosis.stroke.brainstem"),
        t("basicinfo.primary_diagnosis.stroke.embolic"),
        t("basicinfo.primary_diagnosis.stroke.hemorrhagic"),
        t("basicinfo.primary_diagnosis.stroke.thrombotic"),
        t("basicinfo.primary_diagnosis.stroke.tia"),
        t("basicinfo.primary_diagnosis.stroke.unknown"),
      ],
    },
    {
      category: "Other",
      items: [
        t("basicinfo.primary_diagnosis.other.abi"),
        t("basicinfo.primary_diagnosis.other.autism"),
        t("basicinfo.primary_diagnosis.other.covid"),
        t("basicinfo.primary_diagnosis.other.epilepsy"),
      ],
    },
  ];

  const [returnedData, setReturnedData] = useState([]);
  const {
    toggleFromSelectedList,
    switchSingleSelection,
    selectForTherapist,
    addOther,
    selectedData,
    initialize,
  } = useListSelector({ setInitialData: setReturnedData, initialData: returnedData });

  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    location.state?.fromPatientControl
      ? setOnboardingPercent(20)
      : setOnboardingPercent(15);
    setReturnedData(types);
    initialize(types);
  }, [t]);

  const [isLoading, setIsLoading] = useState(false);
  const [patientTypes, setPatientTypes] = useState(null);
  const [errorMessage, setErrorMessage] = useState(false);

  const { register, handleSubmit, errors, setError } = useForm({
    resolver: yupResolver(PatientSchema),
  });

  useEffect(() => {
    getPatientTypes().then((res) => {
      const { data } = res;
      setPatientTypes(data);
    });
  }, []);

  const onSubmit = async (data) => {
    if (
      selectedData["TBI/Concussion"] &&
      selectedData["TBI/Concussion"].length === 0 &&
      selectedData["Dementia"].length === 0 &&
      selectedData["Stroke"].length === 0 &&
      selectedData["Other"].length === 0
    ) {
      setErrorMessage(true);
      return;
    }

    for (const key in selectedData) {
      if (selectedData[key].length > 0) {
        data["patientType"] = key;
        data["patientSubType"] = selectedData[key][0];
      }
    }
    data.patientIdForPatientInviteTP = location.state?.patientIdForPatientInviteTP ?? "";
    try {
      setIsLoading(true);
      const responsePatientData = await registerPatient2(data);
      setPatient(responsePatientData.data);
      await createGoals({ patientId: responsePatientData.data.patientId });
      const getPatientListResponse = await getPatientList();
      setPatientList(getPatientListResponse.data);
      if ("Stroke" == data.patientType) {
        history.push("/RecentStroke1", { fromPatientControl: location.state?.fromPatientControl });
      } else if ("TBI/Concussion" == data.patientType) {
        history.push("/RecentTBI1", { fromPatientControl: location.state?.fromPatientControl });
      } else {
        history.push("/RecentCondition1", { fromPatientControl: location.state?.fromPatientControl });
      }
    } catch (err) {
      setIsLoading(false);
      console.error(err);
      history.push("/oops");
    }
  };

  if (!user) {
    return <Redirect to={{ pathname: "/signIn", state: { from: location } }} />;
  }

  return (
    <>
      <OnboardingStepHeader text={t("patientinfo.header")} />
      <Sallie text={t("patientinfo.sallie")} />
      <Form
        className="mt-4"
        autoComplete="on"
        action="/post"
        method="POST"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Form.Row>
          <Col md={6}>
            <LabelledInput
              name="firstName"
              required
              label={t("basicinfo.name.first")}
              autoComplete="given-name"
              inputRef={register}
              errors={errors}
            />
          </Col>
          <Col md={6}>
            <LabelledInput
              name="lastName"
              autoComplete="family-name"
              label={t("basicinfo.name.last")}
              required
              inputRef={register}
              errors={errors}
            />
          </Col>
          <Col md={6}>
            <Form.Group controlId="gender">
              <Form.Label>
                {t("basicinfo.gender.label")}
                <span className="required">*</span>
              </Form.Label>
              <Form.Control name="gender" as="select" ref={register} isInvalid={errors.gender}>
                <option value=""></option>
                <option value="male">{t("basicinfo.gender.male")}</option>
                <option value="female">{t("basicinfo.gender.female")}</option>
                <option value="other">{t("basicinfo.gender.other")}</option>
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {errors.gender && errors.gender.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <LabelledInput
              name="dob"
              type="DOB"
              label={t("basicinfo.dob")}
              required
              inputRef={register}
              errors={errors}
            />
          </Col>
          <Col md={6}>
            <LabelledInput
              name="externalId"
              label={`${t("patientinfo.id")} ${t("patientinfotherapist.id_info")}`}
              inputRef={register}
              errors={errors}
            />
          </Col>

          <Col md={12}>
            <h6>
              {t("patientinfotherapist.new_patient.label")}
              <span className="required">*</span>
            </h6>
            <Form.Group controlId="patientType">
              {[
                { label: t("patientinfotherapist.new_patient.new"), value: "Therapist/newPatient" },
                { label: t("patientinfotherapist.new_patient.current"), value: "Therapist/curPatient" },
              ].map((relationship, index) => (
                <Form.Check
                  key={index}
                  inline
                  label={relationship.label}
                  value={relationship.value}
                  type="radio"
                  name="patientType"
                  id={`inline-radio-relationship-type-${relationship.value}`}
                  ref={register}
                />
              ))}
              {errors.patientType && (
                <Form.Control.Feedback className="d-block" type="invalid">
                  {t("patientinfotherapist.new_patient.error")}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group controlId="comments">
              <h6>{t("patientinfotherapist.comments")}</h6>
              <Form.Control
                ref={register}
                type="text"
                name="comments"
                isInvalid={errors.comments}
              />
              <Form.Control.Feedback type="invalid">
                {errors.comments && errors.comments.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Form.Row>

        <Form.Group>
          {patientTypes && (
            <>
              <h6>
                {t("basicinfo.primary_diagnosis.label")}
                <span className="required">*</span>
              </h6>
              <Col md={12} className="mt-3">
                <div className="flex flex-col md:flex-row">
                  {returnedData.map((itemList, i) => {
                    const { category } = itemList;
                    const categoryKeyMap = {
                      "TBI/Concussion": "tbi",
                      "Dementia": "dementia",
                      "Stroke": "stroke",
                      "Other": "other",
                    };
                    const categoryKey = categoryKeyMap[category] || category.toLowerCase();
                    return (
                      <StrokeListSelecter
                        key={category}
                        category={category}
                        message={t(`basicinfo.primary_diagnosis.${categoryKey}.category`)}
                        list={itemList.items}
                        index={i}
                        isTherapist={true}
                        selectedList={selectedData[category]}
                        toggleFromSelectedList={toggleFromSelectedList}
                        selectForTherapist={selectForTherapist}
                        addOther={addOther}
                      />
                    );
                  })}
                </div>
              </Col>
            </>
          )}
          {errorMessage && (
            <Form.Control.Feedback style={{ display: "block" }} type="invalid">
              {t("patientinfotherapist.choose.error")}
            </Form.Control.Feedback>
          )}
        </Form.Group>

        <Col md={12}>
          <SubmitButton disabled={isLoading} type="submit">
            {t("patientinfo.confirm")}
          </SubmitButton>
        </Col>

        {errors.status && (
          <Form.Control.Feedback className="d-block" type="invalid">
            {errors.status && errors.status.message}
          </Form.Control.Feedback>
        )}
      </Form>
    </>
  );
};

export default PatientInfo;